import { saveAs } from 'file-saver';
import Logo from '../../../assets/Logo-Negro.png';
import FirmaEmpresa from '../../../assets/firma.jpeg';

// require() en vez de import: en build de producción, webpack analiza estáticamente los
// bindings nombrados de un `import` y falla si no reconoce el patrón de exports del CJS de
// `docx` ("only default export is available"). require() evita ese análisis por completo —
// devuelve el objeto real en runtime, ya verificado con Document/WidthType/BorderStyle presentes.
const docxLib = require('docx');
const {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  ImageRun,
  PageBreak,
  AlignmentType,
  WidthType,
  BorderStyle,
  VerticalAlign,
} = docxLib;

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const DIEZ_A_DIECINUEVE = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];

/** Convierte un número entero (1-99) a su forma en palabras, en mayúsculas, para uso legal ("TRES (3) día/s"). */
const numeroEnPalabras = (n) => {
  const num = parseInt(n, 10);
  if (!num || num < 1) return '';
  if (num < 10) return UNIDADES[num].toUpperCase();
  if (num < 20) return DIEZ_A_DIECINUEVE[num - 10].toUpperCase();
  if (num === 20) return 'VEINTE';
  if (num < 30) return `VEINTI${UNIDADES[num - 20]}`.toUpperCase();
  const decena = Math.floor(num / 10);
  const unidad = num % 10;
  if (num < 100) return (unidad ? `${DECENAS[decena]} Y ${UNIDADES[unidad]}` : DECENAS[decena]).toUpperCase();
  return String(num);
};

const partesFecha = (fechaString) => {
  if (typeof fechaString === 'string' && fechaString.includes('-')) {
    const [year, month, day] = fechaString.split('T')[0].split('-');
    return { year, month, day };
  }
  if (fechaString instanceof Date) {
    return {
      year: String(fechaString.getFullYear()),
      month: String(fechaString.getMonth() + 1).padStart(2, '0'),
      day: String(fechaString.getDate()).padStart(2, '0'),
    };
  }
  return null;
};

/** YYYY-MM-DD / ISO / Date -> "12 de agosto de 2026" */
const formatearFechaLarga = (fechaString) => {
  const p = partesFecha(fechaString);
  if (!p) return '__________';
  return `${parseInt(p.day, 10)} de ${MESES[parseInt(p.month, 10) - 1]} de ${p.year}`;
};

/** YYYY-MM-DD / ISO / Date -> "12/08/2026" */
const formatearFechaCorta = (fechaString) => {
  const p = partesFecha(fechaString);
  if (!p) return '__________';
  return `${p.day}/${p.month}/${p.year}`;
};

const construirTexto = ({ tipo, informeNumero, fechaInforme, faltaCaratulada, diasSuspension, fechaDesde, fechaReintegro }) => {
  const nroInforme = String(informeNumero || '____');
  const fechaInformeCorta = formatearFechaCorta(fechaInforme);
  const falta = (faltaCaratulada || '').trim() || '__________';

  if (tipo === 'apercibimiento') {
    return `Por medio de la presente se le notifica que, conforme a los hechos constatados y antecedentes obrantes bajo Informe Nº${nroInforme}, de fecha ${fechaInformeCorta}, esta empresa ha resuelto aplicarle sanción disciplinaria consistente en APERCIBIMIENTO, en virtud de incumplimiento observado, caratulado como "${falta}". La presente medida se adopta conforme las facultades disciplinarias previstas en la Ley de Contrato de Trabajo Nº 20.744 y Convenio Colectivo de Trabajo Nº 460/73, dejándose expresa constancia que deberá adecuar su conducta al estricto cumplimiento de las normas internas, procedimientos operativos y obligaciones inherentes a sus tareas. Así mismo, se le hace saber que la reiteración de hechos similares podrá dar lugar a la aplicación de sanciones de mayor gravedad.`;
  }

  const diasPalabras = numeroEnPalabras(diasSuspension);
  const diasNumero = parseInt(diasSuspension, 10) || 0;
  const fechaDesdeCorta = formatearFechaCorta(fechaDesde);
  const fechaReintegroCorta = formatearFechaCorta(fechaReintegro);

  return `Por medio de la presente se le notifica formalmente que esta empresa ha resuelto aplicarle una sanción disciplinaria en virtud de la falta laboral verificada y acreditada mediante Informe Nº${nroInforme}, de fecha ${fechaInformeCorta}, consistente en ${falta}, habiéndose sustanciado el procedimiento interno correspondiente, se le otorgó en legal tiempo y forma la posibilidad de efectuar su descargo y ejercer plenamente su derecho de defensa. Analizados los antecedentes obrantes, los mismos no resultaron suficientes para desvirtuar la responsabilidad que le corresponde respecto de los hechos constatados. La conducta observada constituye un incumplimiento a las obligaciones emergentes del contrato de trabajo, configurando una transgresión a los deberes de diligencia, colaboración, buena fe y obediencia establecidos en los artículos 62, 63, 84 y concordantes de la Ley de Contrato de Trabajo N.º 20.744; así como a las disposiciones disciplinarias y de control previstas en el Convenio Colectivo de Trabajo N.º 460/73 aplicable a la actividad. En consecuencia, y teniendo en consideración la gravedad de los hechos y los antecedentes existentes en su legajo personal, esta empresa dispone la aplicación de una sanción disciplinaria consistente en suspensión sin goce de haberes por el término de ${diasPalabras} (${diasNumero}) día/s, a cumplirse conforme cronograma dispuesto por la empresa, el ${fechaDesdeCorta}, debiendo reintegrarse a sus tareas el día ${fechaReintegroCorta}. Asimismo, se lo INTIMA a adecuar en forma inmediata su conducta al estricto cumplimiento de las normas internas de trabajo, procedimientos operativos y obligaciones convencionales y legales vigentes, dejándose expresa constancia de que la reiteración de hechos similares o cualquier nuevo incumplimiento será considerado falta grave en los términos del artículo 242 de la Ley de Contrato de Trabajo N.º 20.744.`;
};

const cargarBytes = async (url) => {
  const respuesta = await fetch(url);
  return new Uint8Array(await respuesta.arrayBuffer());
};

// Funciones (no constantes de módulo): `docx` recién se resuelve/evalúa cuando esto se llama,
// así que si el paquete falla en cargar, solo se rompe la generación del Word, no toda la app.
const bordeFino = () => ({ style: BorderStyle.SINGLE, size: 4, color: '000000' });
const sinBorde = () => ({ style: BorderStyle.NONE, size: 0, color: 'FFFFFF' });
const sinBordesTabla = () => {
  const b = sinBorde();
  return { top: b, bottom: b, left: b, right: b, insideHorizontal: b, insideVertical: b };
};

/** Tabla de encabezado estilo F031/F041/F042: logo | título del documento | código y versión */
const construirEncabezado = (logoBytes, codigoDoc, tituloDoc) => new Table({
  width: { size: 100, type: WidthType.PERCENTAGE },
  borders: { top: bordeFino(), bottom: bordeFino(), left: bordeFino(), right: bordeFino(), insideHorizontal: sinBorde(), insideVertical: bordeFino() },
  rows: [
    new TableRow({
      children: [
        new TableCell({
          width: { size: 22, type: WidthType.PERCENTAGE },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 100, bottom: 100, left: 100, right: 100 },
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new ImageRun({ type: 'png', data: logoBytes, transformation: { width: 95, height: 45 } })],
            }),
          ],
        }),
        new TableCell({
          width: { size: 56, type: WidthType.PERCENTAGE },
          verticalAlign: VerticalAlign.CENTER,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({ text: tituloDoc, bold: true, size: 26 })],
            }),
          ],
        }),
        new TableCell({
          width: { size: 22, type: WidthType.PERCENTAGE },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 60, bottom: 60, left: 60, right: 60 },
          children: [
            new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 20 }, children: [new TextRun({ text: codigoDoc, bold: true, size: 18 })] }),
            new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 20 }, children: [new TextRun({ text: 'VERSIÓN 01', bold: true, size: 15 })] }),
            new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: 'REVISIÓN 16/06/26', bold: true, size: 15 })] }),
          ],
        }),
      ],
    }),
  ],
});

/** Bloque de firma: imagen (si hay) + línea + rótulo, y opcionalmente una línea extra debajo (ej. fecha a mano) */
const bloqueFirma = ({ label, firmaBytes, extraLinea }) => {
  const hijos = [];
  if (firmaBytes) {
    hijos.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [new ImageRun({ type: 'jpg', data: firmaBytes, transformation: { width: 118, height: 81 } })],
    }));
  } else {
    // Espacio en blanco: se firma a mano sobre papel impreso
    hijos.push(new Paragraph({ spacing: { after: 520 }, children: [] }));
  }
  hijos.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    border: { top: { style: BorderStyle.SINGLE, size: 4, color: '000000', space: 4 } },
    spacing: { before: 20 },
    children: [new TextRun({ text: label, size: 16 })],
  }));
  if (extraLinea) {
    hijos.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 200 }, children: [new TextRun({ text: extraLinea, size: 18 })] }));
  }
  return hijos;
};

/** Fila con dos firmas lado a lado (empresa + colaborador) en una tabla sin bordes */
const filaFirmasDobles = (firmaBytes) => new Table({
  width: { size: 100, type: WidthType.PERCENTAGE },
  borders: sinBordesTabla(),
  rows: [
    new TableRow({
      children: [
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          margins: { right: 300 },
          children: bloqueFirma({ label: 'Firma de empresa', firmaBytes }),
        }),
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          margins: { left: 300 },
          children: bloqueFirma({ label: 'Firma del colaborador', firmaBytes: null, extraLinea: 'Fecha de firma: _______________' }),
        }),
      ],
    }),
  ],
});

const construirPagina = ({ variante, encabezado, fechaNotifLarga, empleado, legajo, texto, cierre, firmaBytes }) => {
  const hijos = [
    encabezado,
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 160, after: 160 },
      children: [new TextRun({
        text: variante === 'legajo' ? 'EJEMPLAR PARA LEGAJO DEL COLABORADOR' : 'EJEMPLAR DE NOTIFICACIÓN',
        italics: true,
        size: 16,
        color: '595959',
      })],
    }),
    new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 200 }, children: [new TextRun({ text: `Córdoba, ${fechaNotifLarga}`, size: 20 })] }),
    new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: 'Sr/Sra: ', bold: true, size: 20 }), new TextRun({ text: empleado, size: 20 })] }),
    new Paragraph({ spacing: { after: 240 }, children: [new TextRun({ text: 'Legajo N°: ', bold: true, size: 20 }), new TextRun({ text: String(legajo || ''), size: 20 })] }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [new TextRun({ text: 'NOTIFICACIÓN DE SANCIÓN DISCIPLINARIA', bold: true, size: 24 })],
    }),
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 240, line: 300 },
      children: [new TextRun({ text: texto, size: 20 })],
    }),
    new Paragraph({ spacing: { after: 500 }, children: [new TextRun({ text: cierre, size: 20 })] }),
  ];

  if (variante === 'legajo') {
    hijos.push(...bloqueFirma({ label: 'Firma de empresa', firmaBytes }));
  } else {
    hijos.push(filaFirmasDobles(firmaBytes));
  }

  return hijos;
};

/**
 * Genera y descarga el Word (.docx) de notificación de sanción disciplinaria (F041 Apercibimiento / F042 Suspensión).
 * El archivo tiene 2 hojas: una para el legajo (solo firma de empresa, ya firmada) y otra de notificación
 * (firma de empresa + espacio para que el colaborador firme y feche a mano sobre el papel impreso).
 */
export const generateSancionWord = async ({
  tipo = 'apercibimiento', // 'apercibimiento' | 'suspension'
  informeNumero = '',
  fechaInforme = '',
  empleado = '',
  legajo = '',
  faltaCaratulada = '',
  diasSuspension = null,
  fechaDesde = '',
  fechaReintegro = '',
  fechaNotificacion = ''
}) => {
  const [logoBytes, firmaBytes] = await Promise.all([cargarBytes(Logo), cargarBytes(FirmaEmpresa)]);

  const esApercibimiento = tipo === 'apercibimiento';
  const codigoDoc = esApercibimiento ? 'F041' : 'F042';
  const tituloDoc = esApercibimiento ? 'APERCIBIMIENTO' : 'SANCIONES';
  const texto = construirTexto({ tipo, informeNumero, fechaInforme, faltaCaratulada, diasSuspension, fechaDesde, fechaReintegro });
  const cierre = esApercibimiento ? 'Notifíquese y agregue a su legajo personal.' : 'Notificación:';
  const fechaNotifLarga = formatearFechaLarga(fechaNotificacion);

  const datosPagina = { fechaNotifLarga, empleado, legajo, texto, cierre, firmaBytes };

  const doc = new Document({
    sections: [{
      properties: { page: { margin: { top: 720, bottom: 720, left: 850, right: 850 } } },
      children: [
        ...construirPagina({ variante: 'legajo', encabezado: construirEncabezado(logoBytes, codigoDoc, tituloDoc), ...datosPagina }),
        new Paragraph({ children: [new PageBreak()] }),
        ...construirPagina({ variante: 'notificacion', encabezado: construirEncabezado(logoBytes, codigoDoc, tituloDoc), ...datosPagina }),
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const nombreArchivo = `Sancion_${codigoDoc}_Legajo${legajo || 'SN'}_Informe${informeNumero || 'SN'}.docx`;
  saveAs(blob, nombreArchivo);
};

export default generateSancionWord;
